import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { PlatformSurvey, PlatformSurveyQuestion } from "@/hooks/usePlatformSurveys";

/**
 * Fallback grouping key for cross-wave survey comparison.
 *
 * The comparison view bucketed questions exclusively by group_question_key and
 * silently dropped any question without one. All 6 rows in
 * platform_survey_questions have group_question_key = NULL, so mergedQuestions
 * was always [] and the table rendered empty. Degrade gracefully by matching on
 * normalized question_text instead.
 */
function groupingKeyFor(q: { group_question_key?: string | null; question_text?: string | null }): string | null {
  if (q.group_question_key) return q.group_question_key;
  const normalized = (q.question_text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized ? `text:${normalized}` : null;
}

export type SurveyAnswer = {
  id: string;
  response_id: string;
  question_id: string;
  answer_text: string | null;
  answer_number: number | null;
  answer_choice: any | null;
  user_id: string;
  user_full_name: string | null;
  user_role: string | null;
  wave_label: string;
};

export type SurveyResultsStats = {
  assigned: number;
  completed: number;
  responseRate: number;
  waveCount: number;
};

export type SurveyResultsFilters = {
  waveLabel?: string;
  role?: string;
};

type ResultsReturn = {
  stats: SurveyResultsStats;
  waves: string[];
  questions: PlatformSurveyQuestion[];
  answersByQuestionId: Record<string, SurveyAnswer[]>;
};

async function fetchAnswersForResponses(
  responseIds: string[]
): Promise<{ answers: any[] }> {
  if (responseIds.length === 0) return { answers: [] };
  const { data, error } = await supabase
    .from("platform_survey_answers")
    .select("*")
    .in("response_id", responseIds);
  if (error) throw error;
  return { answers: data || [] };
}

export function useSurveyResults(
  surveyId: string | null,
  filters: SurveyResultsFilters
) {
  return useQuery({
    queryKey: ["survey_results", surveyId, filters],
    enabled: !!surveyId,
    queryFn: async (): Promise<ResultsReturn> => {
      // Assignments (for stats + wave list)
      const { data: assignmentsAll, error: aErr } = await supabase
        .from("platform_survey_assignments")
        .select("id, wave_label")
        .eq("survey_id", surveyId!);
      if (aErr) throw aErr;

      const allWaves = Array.from(
        new Set((assignmentsAll || []).map((a: any) => a.wave_label))
      ).sort();

      const filteredAssignments =
        filters.waveLabel && filters.waveLabel !== "__all__"
          ? (assignmentsAll || []).filter(
              (a: any) => a.wave_label === filters.waveLabel
            )
          : assignmentsAll || [];

      // Responses
      let responsesQuery = supabase
        .from("platform_survey_responses")
        .select("id, user_id, wave_label, completed_at")
        .eq("survey_id", surveyId!);
      if (filters.waveLabel && filters.waveLabel !== "__all__") {
        responsesQuery = responsesQuery.eq("wave_label", filters.waveLabel);
      }
      const { data: responses, error: rErr } = await responsesQuery;
      if (rErr) throw rErr;

      const completedResponses = (responses || []).filter(
        (r: any) => r.completed_at !== null
      );

      // Profiles for role filter and respondent labels
      const userIds = Array.from(
        new Set(completedResponses.map((r: any) => r.user_id))
      );
      let profilesById: Record<
        string,
        { role: string | null; full_name: string | null }
      > = {};
      if (userIds.length > 0) {
        const { data: profiles, error: pErr } = await supabase
          .from("profiles")
          .select("id, role, full_name")
          .in("id", userIds);
        if (pErr) throw pErr;
        profilesById = Object.fromEntries(
          (profiles || []).map((p: any) => [
            p.id,
            { role: p.role, full_name: p.full_name },
          ])
        );
      }

      // Apply role filter
      const roleFiltered =
        filters.role && filters.role !== "__all__"
          ? completedResponses.filter(
              (r: any) => profilesById[r.user_id]?.role === filters.role
            )
          : completedResponses;

      // Questions
      const { data: questions, error: qErr } = await supabase
        .from("platform_survey_questions")
        .select("*")
        .eq("survey_id", surveyId!)
        .order("display_order", { ascending: true });
      if (qErr) throw qErr;

      // Answers
      const responseIds = roleFiltered.map((r: any) => r.id);
      const { answers } = await fetchAnswersForResponses(responseIds);

      const responseMetaById: Record<
        string,
        { user_id: string; wave_label: string }
      > = Object.fromEntries(
        roleFiltered.map((r: any) => [r.id, { user_id: r.user_id, wave_label: r.wave_label }])
      );

      const answersByQuestionId: Record<string, SurveyAnswer[]> = {};
      (questions || []).forEach((q: any) => {
        answersByQuestionId[q.id] = [];
      });
      (answers || []).forEach((a: any) => {
        const meta = responseMetaById[a.response_id];
        if (!meta) return;
        const profile = profilesById[meta.user_id];
        const enriched: SurveyAnswer = {
          id: a.id,
          response_id: a.response_id,
          question_id: a.question_id,
          answer_text: a.answer_text,
          answer_number:
            a.answer_number !== null && a.answer_number !== undefined
              ? Number(a.answer_number)
              : null,
          answer_choice: a.answer_choice,
          user_id: meta.user_id,
          user_full_name: profile?.full_name ?? null,
          user_role: profile?.role ?? null,
          wave_label: meta.wave_label,
        };
        if (!answersByQuestionId[a.question_id]) {
          answersByQuestionId[a.question_id] = [];
        }
        answersByQuestionId[a.question_id].push(enriched);
      });

      const assigned = filteredAssignments.length;
      const completed = roleFiltered.length;
      const responseRate = assigned > 0 ? (completed / assigned) * 100 : 0;

      return {
        stats: {
          assigned,
          completed,
          responseRate,
          waveCount: allWaves.length,
        },
        waves: allWaves,
        questions: (questions || []) as PlatformSurveyQuestion[],
        answersByQuestionId,
      };
    },
  });
}

export function useSurveysInGroup(surveyGroupId: string | null) {
  return useQuery({
    queryKey: ["survey_group", surveyGroupId],
    enabled: !!surveyGroupId,
    queryFn: async (): Promise<{ surveys: PlatformSurvey[] }> => {
      const { data, error } = await supabase
        .from("platform_surveys")
        .select("*")
        .or(`survey_group_id.eq.${surveyGroupId},id.eq.${surveyGroupId}`)
        .order("published_at", { ascending: true, nullsFirst: true });
      if (error) throw error;
      return { surveys: (data || []) as PlatformSurvey[] };
    },
  });
}

export function useAggregateSurveyResults(
  surveyGroupId: string | null,
  selectedSurveyIds: string[],
  filters: { role?: string }
) {
  return useQuery({
    queryKey: ["survey_aggregate", surveyGroupId, selectedSurveyIds, filters],
    enabled: !!surveyGroupId && selectedSurveyIds.length > 0,
    queryFn: async (): Promise<ResultsReturn> => {
      // Assignments across selected surveys
      const { data: assignmentsAll, error: aErr } = await supabase
        .from("platform_survey_assignments")
        .select("id, wave_label, survey_id")
        .in("survey_id", selectedSurveyIds);
      if (aErr) throw aErr;

      // Responses across selected surveys
      const { data: responses, error: rErr } = await supabase
        .from("platform_survey_responses")
        .select("id, user_id, wave_label, completed_at, survey_id")
        .in("survey_id", selectedSurveyIds);
      if (rErr) throw rErr;

      const completedResponses = (responses || []).filter(
        (r: any) => r.completed_at !== null
      );

      const userIds = Array.from(
        new Set(completedResponses.map((r: any) => r.user_id))
      );
      let profilesById: Record<
        string,
        { role: string | null; full_name: string | null }
      > = {};
      if (userIds.length > 0) {
        const { data: profiles, error: pErr } = await supabase
          .from("profiles")
          .select("id, role, full_name")
          .in("id", userIds);
        if (pErr) throw pErr;
        profilesById = Object.fromEntries(
          (profiles || []).map((p: any) => [
            p.id,
            { role: p.role, full_name: p.full_name },
          ])
        );
      }

      const roleFiltered =
        filters.role && filters.role !== "__all__"
          ? completedResponses.filter(
              (r: any) => profilesById[r.user_id]?.role === filters.role
            )
          : completedResponses;

      // Questions across selected surveys
      const { data: allQuestions, error: qErr } = await supabase
        .from("platform_survey_questions")
        .select("*")
        .in("survey_id", selectedSurveyIds)
        .order("display_order", { ascending: true });
      if (qErr) throw qErr;

      // Group by group_question_key when present, else by normalized question_text.
      const questionsByKey: Record<string, any[]> = {};
      (allQuestions || []).forEach((q: any) => {
        const key = groupingKeyFor(q);
        if (!key) return;
        if (!questionsByKey[key]) questionsByKey[key] = [];
        questionsByKey[key].push(q);
      });

      // Build merged "representative" questions (use the first occurrence per key)
      const mergedQuestions: PlatformSurveyQuestion[] = Object.entries(
        questionsByKey
      ).map(([_key, qs]) => qs[0]);

      // Answers
      const responseIds = roleFiltered.map((r: any) => r.id);
      const { answers } = await fetchAnswersForResponses(responseIds);

      const responseMetaById: Record<
        string,
        { user_id: string; wave_label: string }
      > = Object.fromEntries(
        roleFiltered.map((r: any) => [
          r.id,
          { user_id: r.user_id, wave_label: r.wave_label },
        ])
      );

      // Map question.id -> group_question_key
      const questionIdToKey: Record<string, string> = {};
      (allQuestions || []).forEach((q: any) => {
        const key = groupingKeyFor(q);
        if (key) questionIdToKey[q.id] = key;
      });

      // Map merged question (first occurrence id) -> bucket
      const keyToRepId: Record<string, string> = {};
      mergedQuestions.forEach((q: any) => {
        const key = groupingKeyFor(q);
        if (key) keyToRepId[key] = q.id;
      });

      const answersByQuestionId: Record<string, SurveyAnswer[]> = {};
      mergedQuestions.forEach((q) => {
        answersByQuestionId[q.id] = [];
      });

      (answers || []).forEach((a: any) => {
        const key = questionIdToKey[a.question_id];
        if (!key) return;
        const repId = keyToRepId[key];
        if (!repId) return;
        const meta = responseMetaById[a.response_id];
        if (!meta) return;
        const profile = profilesById[meta.user_id];
        answersByQuestionId[repId].push({
          id: a.id,
          response_id: a.response_id,
          question_id: repId,
          answer_text: a.answer_text,
          answer_number:
            a.answer_number !== null && a.answer_number !== undefined
              ? Number(a.answer_number)
              : null,
          answer_choice: a.answer_choice,
          user_id: meta.user_id,
          user_full_name: profile?.full_name ?? null,
          user_role: profile?.role ?? null,
          wave_label: meta.wave_label,
        });
      });

      const assigned = (assignmentsAll || []).length;
      const completed = roleFiltered.length;
      const responseRate = assigned > 0 ? (completed / assigned) * 100 : 0;
      const waveCount = new Set(
        (assignmentsAll || []).map((a: any) => a.wave_label)
      ).size;

      return {
        stats: { assigned, completed, responseRate, waveCount },
        waves: [],
        questions: mergedQuestions,
        answersByQuestionId,
      };
    },
  });
}
